/**
 * Admin gear on /my-accounts (v1.5.64) — edits the FIRM-WIDE tunable thresholds behind the
 * dashboard's rules (single-row my_accounts_settings, migration 111).
 *
 * Field state is kept as STRINGS on purpose: a number state snaps a cleared input to 0,
 * which both fails validation confusingly and fights the admin mid-edit. Save stays
 * disabled until every field parses to an integer within bounds; the server re-validates and
 * 400s naming the field (parseMyAccountsSettingsInput), so nothing out of range can land
 * even if this gate drifts.
 */

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Settings2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { myAccountsSettingsApi, type MyAccountsSettings } from "@/lib/api";

/** Mirrors MY_ACCOUNTS_SETTINGS_BOUNDS on the server and the CHECKs in migrations 111 + 112. */
const BOUNDS = { min: 1, max: 90 } as const;

const FIELDS: Array<{
  key: keyof MyAccountsSettings;
  label: string;
  help: string;
}> = [
  {
    key: "stall_threshold_days",
    label: "Stall threshold",
    help: "Days without any activity before a project counts as stalled.",
  },
  {
    key: "stall_severe_days",
    label: "Stall turns red after",
    help: "Idle days at which the stall pill goes from amber to red.",
  },
  {
    key: "going_quiet_days",
    label: "Going quiet after",
    help: "Days since the last email, SMS or meeting before the relationship reads “going quiet”.",
  },
  {
    key: "waiting_on_client_aging_days",
    label: "Waiting-on-client aging",
    help: "Days after which the oldest waiting-on-client follow-up is called out in amber.",
  },
  {
    key: "inbox_unread_amber_days",
    label: "Unread inbox turns yellow after",
    help: "Age of the oldest unread shared-inbox email at which the card's unread bubble goes from green to yellow.",
  },
  {
    key: "inbox_unread_red_days",
    label: "Unread inbox turns red after",
    help: "Age of the oldest unread shared-inbox email at which the unread bubble goes red.",
  },
];

function parseField(value: string): number | null {
  if (!/^\d+$/.test(value.trim())) return null;
  const n = Number(value.trim());
  if (!Number.isInteger(n) || n < BOUNDS.min || n > BOUNDS.max) return null;
  return n;
}

export function MyAccountsSettingsDialog() {
  const [open, setOpen] = useState(false);
  const [values, setValues] = useState<Record<keyof MyAccountsSettings, string> | null>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const settingsQuery = useQuery({
    queryKey: ["my-accounts-settings"],
    queryFn: myAccountsSettingsApi.get,
    enabled: open,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    retry: 1,
  });

  // Seed the draft from the fetched settings once per open (render-time guard, the
  // AdminSignatureDefaultCard pattern) — never re-seed mid-edit.
  if (open && values === null && settingsQuery.data) {
    const d = settingsQuery.data;
    setValues({
      stall_threshold_days: String(d.stall_threshold_days),
      stall_severe_days: String(d.stall_severe_days),
      going_quiet_days: String(d.going_quiet_days),
      waiting_on_client_aging_days: String(d.waiting_on_client_aging_days),
      inbox_unread_amber_days: String(d.inbox_unread_amber_days),
      inbox_unread_red_days: String(d.inbox_unread_red_days),
    });
  }

  const parsed: MyAccountsSettings | null = (() => {
    if (!values) return null;
    const out: Partial<MyAccountsSettings> = {};
    for (const f of FIELDS) {
      const n = parseField(values[f.key]);
      if (n === null) return null;
      out[f.key] = n;
    }
    return out as MyAccountsSettings;
  })();

  // Coherent but probably unintended: everything stalled is already past the red cut, so no
  // amber band exists. Warn, never block — ?days=30 produces the same all-red state today.
  const inverted = parsed !== null && parsed.stall_threshold_days > parsed.stall_severe_days;

  // Same posture for the unread bubble: an amber cut past the red cut just means the yellow
  // band vanishes (unreadAgeSeverity checks red first, so old mail can never render green).
  const unreadInverted =
    parsed !== null && parsed.inbox_unread_amber_days > parsed.inbox_unread_red_days;

  const saveMutation = useMutation({
    mutationFn: (settings: MyAccountsSettings) => myAccountsSettingsApi.save(settings),
    onSuccess: () => {
      toast({ title: "My Accounts settings saved" });
      queryClient.invalidateQueries({ queryKey: ["my-accounts"] });
      queryClient.invalidateQueries({ queryKey: ["my-accounts-firm"] });
      queryClient.invalidateQueries({ queryKey: ["my-accounts-settings"] });
      setOpen(false);
    },
    onError: (err: Error) => {
      toast({ title: "Failed to save settings", description: err.message, variant: "destructive" });
    },
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setValues(null); // re-seed from the server next open
      }}
    >
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" title="My Accounts settings" data-testid="my-accounts-settings-gear">
          <Settings2 className="h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>My Accounts rules</DialogTitle>
          <DialogDescription>
            Firm-wide thresholds behind every card&apos;s Work and Relationship signals. Days,{" "}
            {BOUNDS.min}&ndash;{BOUNDS.max}. Applies to everyone on their next refresh.
          </DialogDescription>
        </DialogHeader>

        {settingsQuery.isLoading || !values ? (
          <div className="py-6 text-sm text-muted-foreground">Loading current settings…</div>
        ) : (
          <div className="flex flex-col gap-4 py-2">
            {FIELDS.map((f) => (
              <div key={f.key} className="flex flex-col gap-1">
                <label className="text-sm font-medium" htmlFor={`mas-${f.key}`}>
                  {f.label}
                </label>
                <div className="flex items-center gap-2">
                  <Input
                    id={`mas-${f.key}`}
                    type="number"
                    min={BOUNDS.min}
                    max={BOUNDS.max}
                    step={1}
                    className="w-24"
                    value={values[f.key]}
                    onChange={(e) => setValues({ ...values, [f.key]: e.target.value })}
                  />
                  <span className="text-sm text-muted-foreground">days</span>
                </div>
                <p className="text-xs text-muted-foreground">{f.help}</p>
              </div>
            ))}

            {inverted && (
              <p className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                The stall threshold is above the red cut — every stalled project will show red, with
                no amber band. Saving is allowed if that&apos;s intended.
              </p>
            )}

            {unreadInverted && (
              <p className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                The unread yellow cut is above the red cut — the bubble will jump straight from
                green to red, with no yellow band. Saving is allowed if that&apos;s intended.
              </p>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} disabled={saveMutation.isPending}>
            Cancel
          </Button>
          <Button
            onClick={() => parsed && saveMutation.mutate(parsed)}
            disabled={parsed === null || saveMutation.isPending}
          >
            {saveMutation.isPending ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
